import moment from "moment";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";

/** Labels a version by when it was created, e.g. v20260827-1233. */
export function llmClassifierVersionLabel(createdOn?: string) {
    if (!createdOn) return undefined;
    return `v${moment.utc(createdOn).local().format("YYYYMMDD-HHmm")}`;
}

/** Falls back to the raw identifier when the server matched no user. */
export function llmClassifierVersionCreator(
    version: Pick<LLMClassifierImplementation, "createdBy" | "createdByName">,
) {
    return version.createdByName || version.createdBy || "Unknown user";
}

/** Up to two initials, from a name or an email address. */
export function initials(name: string) {
    return name
        .split(/[\s@._-]+/)
        .filter((part) => part.length > 0)
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

/**
 * An active implementation identifies the version selected for deployment, but it is only
 * published while its classifier is published. Records before this change
 * have an active implementation even if their classifier is not published
 */
export function isLLMClassifierVersionPublished(
    classifierIsPublished: boolean,
    version: Pick<LLMClassifierImplementation, "isActive">,
) {
    return classifierIsPublished && version.isActive === true;
}

/**
 * The pair the comparison panel opens on, given versions newest first: the loaded version against the
 * published one, or against the next older version when it is itself published or nothing is published.
 * With nothing older it stands on both sides, so the panel keeps its selectors.
 */
export function defaultComparisonVersions<
    T extends Pick<LLMClassifierImplementation, "id" | "isActive">,
>(
    versions: T[],
    classifierIsPublished: boolean,
    loadedVersionId?: string,
): { before?: T; after?: T } {
    if (versions.length === 0) return {};

    const after =
        versions.find((version) => version.id === loadedVersionId) ??
        versions[0];
    const published = versions.find((version) =>
        isLLMClassifierVersionPublished(classifierIsPublished, version),
    );
    const before =
        published && published.id !== after.id
            ? published
            : (versions[versions.indexOf(after) + 1] ?? after);

    return { before, after };
}

/**
 * The version an approval request records as its comparison: the pair the panel opens on
 */
export function approvalComparisonVersion<
    T extends Pick<LLMClassifierImplementation, "id" | "isActive">,
>(
    versions: T[],
    classifierIsPublished: boolean,
    loadedVersionId?: string,
): T | undefined {
    const { before, after } = defaultComparisonVersions(
        versions,
        classifierIsPublished,
        loadedVersionId,
    );

    return before && before.id !== after?.id ? before : undefined;
}
